import type { CardState, Position } from '@shared/tabletop/types';
import { CARD_HEIGHT, CARD_WIDTH } from '../constants';
import { sortCardIdsByLayer } from './cardLayering';

export type CardStackDirection = 'compact' | 'right' | 'down' | 'left' | 'up';

const COMPACT_OFFSET = 3;
const FAN_OFFSET = 28;

const DIRECTION_OFFSETS: Record<CardStackDirection, Position> = {
  compact: { x: COMPACT_OFFSET, y: COMPACT_OFFSET },
  right: { x: FAN_OFFSET, y: 0 },
  down: { x: 0, y: FAN_OFFSET },
  left: { x: -FAN_OFFSET, y: 0 },
  up: { x: 0, y: -FAN_OFFSET }
};

const getTopLeftAnchor = (cards: CardState[]): Position =>
  cards.reduce(
    (anchor, card) => ({
      x: Math.min(anchor.x, card.position.x),
      y: Math.min(anchor.y, card.position.y)
    }),
    { x: Number.POSITIVE_INFINITY, y: Number.POSITIVE_INFINITY }
  );

const shiftAnchorForDirection = (
  anchor: Position,
  direction: CardStackDirection,
  cardCount: number
): Position => {
  const offset = DIRECTION_OFFSETS[direction];
  const totalX = offset.x * Math.max(0, cardCount - 1);
  const totalY = offset.y * Math.max(0, cardCount - 1);

  return {
    x: anchor.x + (totalX < 0 ? Math.abs(totalX) : 0),
    y: anchor.y + (totalY < 0 ? Math.abs(totalY) : 0)
  };
};

export const getStackedCardPositions = (
  cardIds: string[],
  cards: Record<string, CardState>,
  direction: CardStackDirection,
  anchor?: Position
): { cardIds: string[]; positions: Record<string, Position> } => {
  const orderedCardIds = sortCardIdsByLayer(cardIds, cards);
  const orderedCards = orderedCardIds
    .map(cardId => cards[cardId])
    .filter((card): card is CardState => Boolean(card));

  if (orderedCards.length === 0) {
    return { cardIds: [], positions: {} };
  }

  const baseAnchor = anchor ?? getTopLeftAnchor(orderedCards);
  const adjustedAnchor = shiftAnchorForDirection(baseAnchor, direction, orderedCards.length);
  const offset = DIRECTION_OFFSETS[direction];

  const positions = orderedCards.reduce<Record<string, Position>>((acc, card, index) => {
    acc[card.id] = {
      x: adjustedAnchor.x + offset.x * index,
      y: adjustedAnchor.y + offset.y * index
    };
    return acc;
  }, {});

  return { cardIds: orderedCards.map(card => card.id), positions };
};

export const getStackAnchorFromCenter = (center: Position): Position => ({
  x: center.x - CARD_WIDTH / 2,
  y: center.y - CARD_HEIGHT / 2
});
